import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Chip, DialogActions, LinearProgress, Tooltip, Typography } from '@mui/material';
import { ActionsContainer } from 'klayowebshared';
import { isEmpty } from 'lodash';
import { DragEvent, forwardRef, useCallback, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Placeholder } from '../../components/Placeholder';
import {
  uploadJobDescriptionErrorKey,
  useUploadJobDescription,
} from '../../hooks/useUploadJobDescription';
import { IIndustryStandardMatches } from '../../interfaces/Job';
import { useLocale } from '../../providers/LocaleProvider';
import { useSnackbar } from '../../providers/SnackbarProvider';
import ChevronRightIcon from '../../resources/images/icons-chevron-right.svg';
import DragIcon from '../../resources/images/icons-drag.svg';
import RemoveIcon from '../../resources/images/icons-remove.svg';
import { KLAYO_COLORS } from '../../themes';
import {
  UploadJobDescriptionSchema,
  UploadJobDescriptionType,
} from '../../utilities/validationSchema';

export type UploadJobDescriptionFileProps = {
  onClose: () => void;
  onUploaded: (data: IIndustryStandardMatches) => void;
};

const defaultValue = {
  file: null,
};

const UploadJobDescriptionFile = (props: UploadJobDescriptionFileProps) => {
  const { onClose, onUploaded } = {
    ...props,
  };

  const { control, formState, handleSubmit, setValue, watch } = useForm<UploadJobDescriptionType>({
    resolver: zodResolver(UploadJobDescriptionSchema),
    defaultValues: defaultValue,
    mode: 'onChange',
  });
  const [progress, setProgress] = useState(0);

  const fileInputRef = useRef<any>(null);

  const uploadedFile = watch('file');

  const { mutateAsync: onUploadJobDescription, isPending: isLoading } = useUploadJobDescription();

  const { showSnackbar } = useSnackbar();
  const { translate } = useLocale();

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dropFiles = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    setValue('file', dropFiles[0], { shouldValidate: true });
  };

  const handleRemoveFile = () => {
    setValue('file', null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onContinue = useCallback(
    async (values: UploadJobDescriptionType) => {
      try {
        const response = await onUploadJobDescription({
          data: values.file,
          onUploadProgress: (event) => {
            const total = event.total;
            const loaded = event.loaded;

            if (!total || !loaded) return;

            const calculator = Math.floor((event.loaded / total) * 100);

            if (calculator <= 99) {
              setProgress(calculator);
            }
          },
        });

        setProgress(100);

        const timer = setTimeout(() => {
          onUploaded(response.data);
          clearTimeout(timer);
        }, 0);
      } catch (error) {
        setProgress(0);
        showSnackbar(translate(uploadJobDescriptionErrorKey(error)), 'error');
      }
    },
    [onUploadJobDescription, onUploaded]
  );

  return (
    <Box component={'form'} onSubmit={handleSubmit(onContinue)}>
      {progress ? (
        <ProgressUpload progress={progress} translate={translate} />
      ) : (
        <Box className={'klayo-upload' + (formState.errors.file ? ' klayo-upload--error' : '')}>
          <Box
            className='klayo-upload_dropzone'
            onDrop={!uploadedFile ? handleDrop : undefined}
            height='408px'
            padding='0'
            display='flex'
            alignItems='center'
            justifyContent='center'
            borderColor={!uploadedFile ? KLAYO_COLORS.NeonBlueNB200 : KLAYO_COLORS.GreyGR300}
            onDragOver={handleDragOver}
            onClick={!uploadedFile ? handleDropZoneClick : undefined}
            sx={{
              backgroundColor: uploadedFile ? KLAYO_COLORS.GreyGR100 : KLAYO_COLORS.WhiteWH900,
              cursor: !uploadedFile ? 'pointer' : 'default',
            }}
          >
            <Placeholder
              text={{
                primary: (
                  <div
                    className='klayo-placeholder_text_secondary'
                    dangerouslySetInnerHTML={{
                      __html: translate('standardsLibrary.clickToUploadPdf'),
                    }}
                  />
                ),
              }}
              primaryTextSize='16px'
              secondaryTextSize='14px'
              spacing={2}
              icon={
                <DragIcon
                  style={{
                    color: uploadedFile ? KLAYO_COLORS.GreyGR400 : KLAYO_COLORS.NeonBlueNB200,
                  }}
                />
              }
              sx={{ pointerEvents: 'none' }}
            />
          </Box>

          {formState.errors.file && (
            <Box
              className='klayo-upload_error'
              sx={{
                padding: '8px 0 0 !important',
              }}
            >
              {formState.errors.file.message}
            </Box>
          )}

          <Box mt={1} className='klayo-placeholder_text_secondary'>
            {translate('standardsLibrary.maxFileSizeMb')}
          </Box>
          {uploadedFile && (
            <Box className='klayo-upload_chipcontainer'>
              <Chip
                key={uploadedFile.name}
                label={uploadedFile.name}
                deleteIcon={<RemoveIcon />}
                onDelete={(e) => handleRemoveFile()}
              />
            </Box>
          )}

          <Controller
            name='file'
            control={control}
            render={({ field }) => (
              <VisuallyHiddenInput
                type='file'
                accept={'.pdf'}
                ref={fileInputRef}
                onChange={(e) => {
                  const files = e.target.files || [];
                  if (isEmpty(files)) return;
                  field.onChange(files[0]);
                }}
              />
            )}
          />

          <DialogActions
            sx={{
              padding: { xs: '24px 0', md: '40px 0' },
            }}
          >
            <ActionsContainer
              actions={[
                {
                  disabled: !uploadedFile || isLoading || formState.errors.file,
                  label: translate('common.continue'),
                  primary: true,
                  variant: 'filled',
                  endIcon: <StyledChevronRightIcon />,
                  type: 'submit',
                  isLoading: isLoading,
                },
                {
                  label: translate('common.cancel'),
                  type: 'button',
                  onClick: onClose,
                  disabled: isLoading,
                },
              ]}
              justifyActions='right'
              onClose={onClose}
            />
          </DialogActions>
        </Box>
      )}
    </Box>
  );
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default UploadJobDescriptionFile;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
  & path:last-child {
    fill: white !important;
  }
`;

const ProgressUpload = forwardRef(
  ({ progress, translate }: { progress: number; translate: (key: string) => string }, ref) => {
    return (
      <Box
        height='408px'
        display='flex'
        justifyContent='center'
        alignItems='center'
        flexDirection='column'
      >
        <Box>
          <Box display='flex' alignItems='center' gap={1} flex={1} width='100%'>
            <Tooltip title={`${progress}%`} arrow>
              <LinearProgress
                variant='determinate'
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: KLAYO_COLORS.NeonBlueNB300,
                    borderRadius: 4,
                  },
                  flex: 1,
                }}
              />
            </Tooltip>
            <Typography
              sx={{
                minWidth: 36,
              }}
              variant='body2'
              color='textSecondary'
            >
              {progress}%
            </Typography>
          </Box>
          <div className='klayo-placeholder_text_secondary' style={{ marginTop: '10px' }}>
            {translate('standardsLibrary.processingJobDescription')}
          </div>
        </Box>
      </Box>
    );
  }
);
ProgressUpload.displayName = 'ProgressUpload';
